import { useTranslation } from 'react-i18next';
import type { ProductStats } from './api';
import { oemName, type Tone } from './format';
import { CARD, IconTile, LABEL, type IconName } from './ui';

interface Tile {
  key: string;
  label: string;
  value: number;
  note: string;
  icon: IconName;
  tone: Tone;
}

/**
 * The four numbers over the list: serials stored, under warranty, ending soon and needing
 * attention. An older service has only the record counts, so only the first tile shows.
 */
export function SummaryTiles({ stats }: { readonly stats: ProductStats }) {
  const { t } = useTranslation();
  const records = stats.records ?? {};
  const products = stats.products ?? records.product ?? 0;
  const oems = (stats.oems ?? []).map((oem) => oem.name || oemName(oem.code)).filter(Boolean).join(', ');
  const tiles: Tile[] = [{
    key: 'serials',
    label: t('Serials stored'),
    value: stats.units?.total ?? records.serial ?? 0,
    note: oems
      ? t('{{products}} products · {{oems}}', { products: products.toLocaleString(), oems })
      : t('{{products}} products', { products: products.toLocaleString() }),
    icon: 'monitor',
    tone: 'info',
  }];
  if (stats.units) {
    const next = stats.next_ending;
    tiles.push({
      key: 'active',
      label: t('Under warranty'),
      value: stats.units.active,
      note: t('at least one active coverage'),
      icon: 'shield',
      tone: 'ok',
    }, {
      key: 'ending',
      label: t('Ending in {{days}} days', { days: stats.ending_soon_days ?? 90 }),
      value: stats.units.ending_soon,
      note: next
        ? t('next ends {{date}}{{oem}}', { date: next.end_date, oem: next.oem ? ` (${oemName(next.oem)})` : '' })
        : t('no active warranty to end'),
      icon: 'clock',
      tone: 'warn',
    });
  }
  if (stats.attention) {
    tiles.push({
      key: 'attention',
      label: t('Needs attention'),
      value: stats.attention.total,
      note: t('not found, needs model no., failed'),
      icon: 'alert',
      tone: 'bad',
    });
  }

  return (
    <section aria-label={t('Summary')} className="grid grid-cols-2 gap-3 md:grid-cols-4">
      {tiles.map((tile) => (
        <div key={tile.key} role="group" aria-label={tile.label} className={`${CARD} p-4`}>
          <div className="mb-2.5 flex items-center gap-2.5">
            <IconTile name={tile.icon} tone={tile.tone} />
            <span className={LABEL}>{tile.label}</span>
          </div>
          <div className="text-[26px] font-extrabold leading-tight text-[#0B1B3D] dark:text-[#e6edf3]">{tile.value.toLocaleString()}</div>
          <div className="mt-0.5 text-xs text-[#5C6B85] dark:text-[#8b949e]">{tile.note}</div>
        </div>
      ))}
    </section>
  );
}
